import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Edit, Trash2, X, Upload } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

interface Category { id: number; name: string; }
interface Variant { size: string; color: string; color_hex: string; price: number | string; stock: number | string; }

const SIZES = ['S', 'M', 'L', 'XL', 'One Size', 'Standard', '1.2kg', '3kg', '4kg', '6kg', '12kg'];
const DEFAULT_COLORS = [
  { name: 'Black', hex: '#000000' }, { name: 'White', hex: '#FFFFFF' }, { name: 'Golden', hex: '#dfc19b' },
  { name: 'Grey', hex: '#9CA3AF' }, { name: 'Brown', hex: '#8B4513' }, { name: 'Blue', hex: '#2563EB' },
  { name: 'Red', hex: '#DC2626' }, { name: 'Green', hex: '#16A34A' }, { name: 'Tan', hex: '#C8A97E' }, { name: 'Purple', hex: '#8E44AD' },
];

function ProductModal({ product, categories, onClose, onSaved }: { product?: any; categories: Category[]; onClose: () => void; onSaved: () => void; }) {
  const isEdit = !!product;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: product?.name || '',
    category_id: product?.category_id || '',
    price: product?.price || '',
    compare_price: product?.compare_price || '',
    description: product?.description || '',
    short_description: product?.short_description || '',
    sku: product?.sku || '',
    stock: product?.stock || 0,
    brand: product?.brand || 'Royal Pet Store',
    material: product?.material || '',
    is_active: product?.is_active !== false,
    is_featured: product?.is_featured || false,
    is_new: product?.is_new || false,
    product_type: product?.product_type || 'other',
    breed: product?.breed || '',
    age: product?.age || '',
    gender: product?.gender || '',
    vaccinated: product?.vaccinated || false,
    pet_type: product?.pet_type || '',
    weight: product?.weight || '',
    flavor: product?.flavor || '',
  });
  const [selectedSizes, setSelectedSizes] = useState<string[]>(product?.sizes || []);
  const [selectedColors, setSelectedColors] = useState<Array<{ name: string; hex: string }>>(product?.colors || []);
  const [customColor, setCustomColor] = useState({ name: '', hex: '#000000' });
  const [variants, setVariants] = useState<Variant[]>(product?.variants || []);

  // Featured image
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState(getImageUrl(product?.featured_image) || '');

  // Gallery images (up to 5 extra)
  const MAX_GALLERY = 5;
  const existingImages: Array<{ id: number; image: string }> = product?.images?.slice(0, MAX_GALLERY) || [];
  const [galleryFiles, setGalleryFiles] = useState<(File | null)[]>(new Array(MAX_GALLERY).fill(null));
  const [galleryPreviews, setGalleryPreviews] = useState<(string | null)[]>(
    existingImages.map(i => getImageUrl(i.image)).concat(new Array(MAX_GALLERY - existingImages.length).fill(null))
  );
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
  };

  const handleGalleryChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const newFiles = [...galleryFiles]; newFiles[index] = file;
    const newPreviews = [...galleryPreviews]; newPreviews[index] = URL.createObjectURL(file);
    setGalleryFiles(newFiles); setGalleryPreviews(newPreviews);
  };

  const removeGallerySlot = (index: number) => {
    const img = existingImages[index];
    if (img) setDeletedImageIds(prev => [...prev, img.id]);
    const newFiles = [...galleryFiles]; newFiles[index] = null;
    const newPreviews = [...galleryPreviews]; newPreviews[index] = null;
    setGalleryFiles(newFiles); setGalleryPreviews(newPreviews);
  };

  const generateVariants = () => {
    const newVariants: Variant[] = [];
    for (const size of selectedSizes) {
      for (const color of selectedColors) {
        const existing = variants.find(v => v.size === size && v.color === color.name);
        newVariants.push(existing || { size, color: color.name, color_hex: color.hex, price: form.price, stock: 10 });
      }
    }
    setVariants(newVariants);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.category_id || !form.price) { toast.error('Please fill required fields'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, String(v)));
      formData.append('sizes', JSON.stringify(selectedSizes));
      formData.append('colors', JSON.stringify(selectedColors));
      formData.append('variants', JSON.stringify(variants));
      if (imageFile) formData.append('featured_image', imageFile);

      let savedId: number;
      if (isEdit) {
        formData.append('_method', 'PUT');
        const res = await api.post(`/admin/products/${product.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        savedId = res.data.id;
        toast.success('Product updated!');
      } else {
        const res = await api.post('/admin/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        savedId = res.data.id;
        toast.success('Product created!');
      }

      // Delete removed gallery images
      for (const imgId of deletedImageIds) {
        await api.delete(`/admin/products/${savedId}/images/${imgId}`).catch(() => {});
      }

      // Upload new gallery images
      for (const file of galleryFiles.filter(Boolean)) {
        const imgForm = new FormData();
        imgForm.append('image', file!);
        await api.post(`/admin/products/${savedId}/images`, imgForm, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => {});
      }

      onSaved();
      onClose();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        (Object.values(errors) as string[][]).flat().forEach(msg => toast.error(msg));
      } else {
        toast.error(err.response?.data?.message || 'Failed to save product');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Product' : 'Add New Product'}</h2>
          <button aria-label="Close" onClick={onClose}><X size={20} className="text-gray-500 hover:text-gray-900" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Product Name *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="e.g., Classic Oxford Shirt" />
            </div>

            <div>
              <label htmlFor="prod-category" className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Category *</label>
              <select id="prod-category" value={form.category_id} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 bg-white">
                <option value="">Select Category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="prod-type" className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Product Type *</label>
              <select id="prod-type" value={form.product_type} onChange={e => setForm(p => ({ ...p, product_type: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 bg-white">
                <option value="other">Other / General</option>
                <option value="pet">Pet (Adoptable)</option>
                <option value="food">Pet Food</option>
                <option value="accessory">Pet Accessory</option>
              </select>
            </div>

            <div>
              <label htmlFor="prod-brand" className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Brand</label>
              <input id="prod-brand" value={form.brand} onChange={e => setForm(p => ({ ...p, brand: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" />
            </div>

            <div>
              <label htmlFor="prod-price" className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Price (Rs.) *</label>
              <input id="prod-price" type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="0.00" />
            </div>

            <div>
              <label htmlFor="prod-compare-price" className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Compare Price (Rs.)</label>
              <input id="prod-compare-price" type="number" value={form.compare_price} onChange={e => setForm(p => ({ ...p, compare_price: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="Original price for strikethrough" />
            </div>

            <div>
              <label htmlFor="prod-stock" className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Stock</label>
              <input id="prod-stock" type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: Number(e.target.value) }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" />
            </div>

            <div>
              <label htmlFor="prod-material" className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Material / Special Info</label>
              <input id="prod-material" value={form.material} onChange={e => setForm(p => ({ ...p, material: e.target.value }))} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900" placeholder="e.g. Leather, Polyester" />
            </div>

            {/* Conditional Fields */}
            {form.product_type === 'pet' && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="md:col-span-3">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Pet Profile Details</h4>
                </div>
                <div>
                  <label htmlFor="pet-breed" className="block text-xs text-gray-500 mb-1">Breed</label>
                  <input id="pet-breed" value={form.breed} onChange={e => setForm(p => ({ ...p, breed: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white" placeholder="e.g. Husky" />
                </div>
                <div>
                  <label htmlFor="pet-age" className="block text-xs text-gray-500 mb-1">Age</label>
                  <input id="pet-age" value={form.age} onChange={e => setForm(p => ({ ...p, age: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white" placeholder="e.g. 2 Months" />
                </div>
                <div>
                  <label htmlFor="pet-gender" className="block text-xs text-gray-500 mb-1">Gender</label>
                  <select id="pet-gender" value={form.gender} onChange={e => setForm(p => ({ ...p, gender: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white">
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="pet-category-val" className="block text-xs text-gray-500 mb-1">Pet Type (Dog/Cat/Bird)</label>
                  <input id="pet-category-val" value={form.pet_type} onChange={e => setForm(p => ({ ...p, pet_type: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white" placeholder="e.g. Dog" />
                </div>
                <div className="flex items-center pt-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.vaccinated} onChange={e => setForm(p => ({ ...p, vaccinated: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-gray-950 focus:ring-0" />
                    <span className="text-xs text-gray-700 font-medium">Fully Vaccinated</span>
                  </label>
                </div>
              </div>
            )}

            {form.product_type === 'food' && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="md:col-span-3">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Food Product details</h4>
                </div>
                <div>
                  <label htmlFor="food-weight" className="block text-xs text-gray-500 mb-1">Net Weight</label>
                  <input id="food-weight" value={form.weight} onChange={e => setForm(p => ({ ...p, weight: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white" placeholder="e.g. 3 kg" />
                </div>
                <div>
                  <label htmlFor="food-flavor" className="block text-xs text-gray-500 mb-1">Flavor</label>
                  <input id="food-flavor" value={form.flavor} onChange={e => setForm(p => ({ ...p, flavor: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white" placeholder="e.g. Tuna" />
                </div>
                <div>
                  <label htmlFor="food-pet-category" className="block text-xs text-gray-500 mb-1">Pet Type (Dog/Cat/Bird)</label>
                  <input id="food-pet-category" value={form.pet_type} onChange={e => setForm(p => ({ ...p, pet_type: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white" placeholder="e.g. Cat" />
                </div>
              </div>
            )}

            {form.product_type === 'accessory' && (
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="md:col-span-2">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Accessory Profile Details</h4>
                </div>
                <div>
                  <label htmlFor="acc-pet-category" className="block text-xs text-gray-500 mb-1">Target Pet Type</label>
                  <input id="acc-pet-category" value={form.pet_type} onChange={e => setForm(p => ({ ...p, pet_type: e.target.value }))} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-950 bg-white" placeholder="e.g. Dog & Cat" />
                </div>
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} className="w-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-gray-900 resize-none" />
            </div>

            {/* Product Images */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-3">Product Images (1 featured + up to 5 gallery)</label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {/* Featured Image Slot */}
                <div className="relative group">
                  <label className="block cursor-pointer">
                    <div className={`aspect-square border-2 border-dashed rounded-lg overflow-hidden flex items-center justify-center transition-colors ${imagePreview ? 'border-gray-300' : 'border-gray-300 hover:border-gray-500 bg-gray-50'}`}>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Featured" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center p-2">
                          <Upload size={18} className="mx-auto text-gray-400 mb-1" />
                          <p className="text-[10px] text-gray-400">Featured</p>
                        </div>
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                  </label>
                  <span className="absolute -top-1.5 -left-1.5 bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm">MAIN</span>
                  {imagePreview && (
                    <button type="button" onClick={() => { setImageFile(null); setImagePreview(''); }} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={10} />
                    </button>
                  )}
                </div>

                {/* Gallery Image Slots */}
                {Array.from({ length: MAX_GALLERY }, (_, i) => (
                  <div key={i} className="relative group">
                    <label className="block cursor-pointer">
                      <div className={`aspect-square border-2 border-dashed rounded-lg overflow-hidden flex items-center justify-center transition-colors ${galleryPreviews[i] ? 'border-gray-300' : 'border-gray-200 hover:border-gray-400 bg-gray-50'}`}>
                        {galleryPreviews[i] ? (
                          <img src={galleryPreviews[i]!} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-center p-2">
                            <Upload size={14} className="mx-auto text-gray-300 mb-1" />
                            <p className="text-[10px] text-gray-300">+Photo</p>
                          </div>
                        )}
                      </div>
                      <input type="file" accept="image/*" onChange={(e) => handleGalleryChange(i, e)} className="hidden" />
                    </label>
                    {galleryPreviews[i] && (
                      <button type="button" onClick={() => removeGallerySlot(i)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-2">Click any slot to upload. First image is the main product image shown on listings.</p>
            </div>

            {/* Size Selector */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-2">Sizes</label>
              <div className="flex flex-wrap gap-2">
                {SIZES.map(size => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size])}
                    className={`px-3 py-1.5 text-xs font-medium border transition-all ${selectedSizes.includes(size) ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-600 hover:border-gray-900'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selector */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-2">Colors</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {DEFAULT_COLORS.map(color => {
                  const isSelected = selectedColors.some(c => c.name === color.name);
                  return (
                    <button
                      key={color.name}
                      type="button"
                      onClick={() => setSelectedColors(prev => isSelected ? prev.filter(c => c.name !== color.name) : [...prev, color])}
                      title={color.name}
                      aria-label={color.name}
                      className={`w-8 h-8 rounded-full border-4 transition-all hover:scale-110 ${isSelected ? 'border-gray-900' : 'border-gray-200'}`}
                      style={{ backgroundColor: color.hex }}
                    />
                  );
                })}
              </div>
              <div className="flex gap-2 items-center">
                <input aria-label="Custom color name" value={customColor.name} onChange={e => setCustomColor(p => ({ ...p, name: e.target.value }))} placeholder="Color name" className="flex-1 border border-gray-300 px-3 py-1.5 text-xs focus:outline-none focus:border-gray-900" />
                <input aria-label="Custom color picker" type="color" value={customColor.hex} onChange={e => setCustomColor(p => ({ ...p, hex: e.target.value }))} className="w-9 h-9 rounded cursor-pointer border border-gray-300" />
                <button type="button" onClick={() => { if (customColor.name) { setSelectedColors(prev => [...prev, { ...customColor }]); setCustomColor({ name: '', hex: '#000000' }); }}} className="px-3 py-1.5 bg-gray-900 text-white text-xs rounded hover:bg-gray-700">Add</button>
              </div>
              {selectedColors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedColors.map(c => (
                    <span key={c.name} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded-full">
                      <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: c.hex }} />
                      {c.name}
                      <button type="button" aria-label={`Remove ${c.name}`} onClick={() => setSelectedColors(prev => prev.filter(x => x.name !== c.name))} className="text-gray-400 hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Variants */}
            {selectedSizes.length > 0 && selectedColors.length > 0 && (
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-medium uppercase tracking-wider text-gray-600">Variants</label>
                  <button type="button" onClick={generateVariants} className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded hover:bg-gray-700 transition-colors">
                    Auto-Generate Variants
                  </button>
                </div>
                {variants.length > 0 && (
                  <div className="overflow-x-auto border border-gray-200 rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Size</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Color</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Price</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">Stock</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {variants.slice(0, 10).map((v, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2">{v.size}</td>
                            <td className="px-3 py-2">
                              <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: v.color_hex }} />
                                {v.color}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <input aria-label={`Price for ${v.size} ${v.color}`} type="number" value={v.price} onChange={e => setVariants(prev => prev.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} className="w-24 border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:border-gray-900" />
                            </td>
                            <td className="px-3 py-2">
                              <input aria-label={`Stock for ${v.size} ${v.color}`} type="number" value={v.stock} onChange={e => setVariants(prev => prev.map((x, j) => j === i ? { ...x, stock: e.target.value } : x))} className="w-20 border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:border-gray-900" />
                            </td>
                            <td className="px-3 py-2">
                              <button type="button" aria-label={`Remove ${v.size} ${v.color} variant`} onClick={() => setVariants(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                            </td>
                          </tr>
                        ))}
                        {variants.length > 10 && <tr><td colSpan={5} className="px-3 py-2 text-center text-gray-400">+{variants.length - 10} more variants</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Flags */}
            <div className="md:col-span-2 flex flex-wrap gap-6">
              {[
                { key: 'is_active', label: 'Active (visible on store)' },
                { key: 'is_featured', label: 'Featured product' },
                { key: 'is_new', label: 'Mark as New Arrival' },
              ].map(flag => (
                <label key={flag.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form[flag.key as keyof typeof form] as boolean}
                    onChange={e => setForm(p => ({ ...p, [flag.key]: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-0"
                  />
                  <span className="text-sm text-gray-700">{flag.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-sm font-medium text-gray-700 hover:border-gray-900 transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="px-8 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-60 flex items-center gap-2">
              {loading && <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />}
              {isEdit ? 'Save Changes' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<any>(null);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const fetchProducts = useCallback((page = 1) => {
    setLoading(true);
    const params: any = { page };
    if (search) params.search = search;
    api.get('/admin/products', { params })
      .then(r => {
        setProducts(r.data.data || r.data);
        if (r.data.meta) setPagination({ current_page: r.data.meta.current_page, last_page: r.data.meta.last_page, total: r.data.meta.total });
        else setPagination({ current_page: r.data.current_page || 1, last_page: r.data.last_page || 1, total: r.data.total || 0 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    api.get('/admin/categories').then(r => setCategories(r.data)).catch(() => {});
    fetchProducts(1);
  }, []);

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (product: any) => {
    try {
      await api.put(`/admin/products/${product.id}`, { is_active: !product.is_active, _method: 'PUT' });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_active: !p.is_active } : p));
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-500 mt-1">{pagination.total} total products</p>
        </div>
        <button onClick={() => { setEditProduct(null); setModalOpen(true); }} className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-gray-700 transition-colors">
          <Plus size={16} /> Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchProducts(1)}
            placeholder="Search products..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:border-gray-900"
          />
        </div>
        <button onClick={() => fetchProducts(1)} className="px-4 py-2 bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors">Search</button>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? Array(5).fill(null).map((_, i) => (
                <tr key={i}><td colSpan={6} className="px-6 py-4"><div className="h-10 bg-gray-100 animate-pulse rounded" /></td></tr>
              )) : products.map(product => (
                <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-14 bg-gray-100 flex-shrink-0 overflow-hidden">
                        {product.featured_image && <img src={getImageUrl(product.featured_image)} alt={product.name} className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">SKU: {product.sku || 'N/A'}</p>
                        <div className="flex gap-1.5 mt-1">
                          {product.is_featured && <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">Featured</span>}
                          {product.is_new && <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">New</span>}
                          {product.product_type && <span className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded uppercase">{product.product_type}</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{product.category?.name}</td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-semibold text-gray-900">Rs.{Number(product.price).toLocaleString()}</p>
                    {product.compare_price && <p className="text-xs text-gray-400 line-through">Rs.{Number(product.compare_price).toLocaleString()}</p>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{product.stock}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => toggleActive(product)} className={`text-xs font-medium px-2.5 py-1 rounded-full ${product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button aria-label="Edit product" onClick={() => { setEditProduct(product); setModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors">
                        <Edit size={15} />
                      </button>
                      <button aria-label="Delete product" onClick={() => handleDelete(product.id, product.name)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-100">
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
              <button key={page} onClick={() => fetchProducts(page)} className={`w-9 h-9 text-xs font-medium border ${page === pagination.current_page ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-700 hover:border-gray-900'}`}>{page}</button>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <ProductModal
          product={editProduct}
          categories={categories}
          onClose={() => { setModalOpen(false); setEditProduct(null); }}
          onSaved={fetchProducts}
        />
      )}
    </div>
  );
}
